/* eslint-disable @nx/enforce-module-boundaries */
import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Dialog, DialogTitle, DialogContent, DialogContentText, Box, Paper, useTheme, Tooltip } from '@mui/material';
import iIcon from 'packages/novous_web/src/assets/icons/i.svg';
import route from 'packages/novous_web/src/assets/icons/route.svg';
import meter from 'packages/novous_web/src/assets/icons/Meter.svg';
import glocation from 'packages/novous_web/src/assets/icons/glocation.svg';
import Idle from 'packages/novous_web/src/assets/icons/Idle.svg';
import { ReactComponent as MapIcon } from 'packages/novous_web/src/assets/icons/Map Stroke.svg';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import MapPreview from '../../Home/components/MapPreview';
import { Console } from 'console';
import { MAX } from 'uuid';

interface VehicleDashboardCardProps {
    startDate: string;
    endDate: string;
    tilesObj?: TilesObjType;
    fleetId: string;
    vehicleId?: string; // This will be used as vehicleRegistrationNo

}




interface VehicleDetails {
    totalKilometers: number;
    totalAverageRunning: number;
    totalAverageIdleTime: number; // in seconds (total idle time over the given period)
    currentAddress: string;
    vehicleName: string;
    totalAverageDrivingTime: number; // in seconds (total driving time over the given period)
    currentLatitude: string;
    currentLongitude: string;
    latestUpdatedDate: string;
    occupancyCount?: number; // Assuming this is part of the data
}

/**
 * Calculate the difference in days between two dates.
 * Returns at least 1 to avoid division by zero.
 */
const getDaysDifference = (startDateStr: string, endDateStr: string): number => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    const diffInMs = Math.abs(endDate.getTime() - startDate.getTime());
    const days = diffInMs / (1000 * 3600 * 24);
    return days || 1;
};

export const formatTimePerDay = (seconds: number): React.ReactNode => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return (
        <>
            <span style={{ fontWeight: "bold" }}>{hours}</span>
            <span style={{ fontWeight: "lighter" }}> Hr </span>
            <span style={{ fontWeight: "bold" }}>{minutes}</span>
            <span style={{ fontWeight: "lighter" }}> mins / day</span>
        </>
    );
};

/**
 * Helper function: returns a React element that displays distance
 * (rounded to integer) per day, in kilometers.
 */
export const formatDistancePerDay = (distance: number): React.ReactNode => {
    return (
        <>
            <span style={{ fontWeight: "bold" }}>{Math.round(distance)}</span>
            <span style={{ fontWeight: "lighter" }}> Km / day</span>
        </>
    );
};

/**
 * Convert total seconds over a period into a daily average,
 * and then format that daily value as "X Hr Y mins/day".
 */
const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const remainingSeconds = totalSeconds % 3600;
    const minutes = Math.floor(remainingSeconds / 60);
    return `${hours} Hr ${minutes} mins/day`;
};

type TilesObjType = {
    [key: string]: {
        name: string;
        status: boolean;
    };
};


interface TileData {
    icon: string;
    heading: string;
    value: React.ReactNode;
    changeTag: boolean;

}

const VehicleDashboardCard: React.FC<VehicleDashboardCardProps> = ({
    startDate,
    endDate,
    fleetId,
    tilesObj,
    vehicleId,

}) => {
    // Local state to manage dialog open/close
    const [isVehicleLocationModalOpen, setIsVehicleLocationModalOpen] = useState(false);
    // State to hold the vehicle details data for the modal
    const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
    console.log('tilesObj in VehicleDashboardCard:', tilesObj);

    // Log the received props each time they change.
    useEffect(() => {
        console.log('VehicleDashboardCard props:', { startDate, endDate, fleetId, vehicleId });
    }, [startDate, endDate, fleetId, vehicleId]);

    const {
        data: vehicleDetailsData,
        error: vehicleDetailsError,
        loading: vehicleDetailsLoading,
        fetchData: fetchVehicleDetailsData,
    } = useAxios<{ data: VehicleDetails }>(API_ENDPOINTS.directories.VEHICLE_DETAILS, 'POST');

    let tilesData: TileData[] = [
        {
            icon: route,
            heading: "Total KM Driven",
            value: "",
            changeTag: false,
        },
        {
            icon: glocation,
            heading: "Average KM Running",
            value: "",
            changeTag: true,
        },
        {
            icon: meter,
            heading: "Average Idling Time",
            value: "",
            changeTag: true,
        },
        {
            icon: Idle,
            heading: "Average Driving Time",
            value: "",
            changeTag: false,
        },
        {
            icon: Idle,
            heading:"Occupancy Count",
            value: "0",
            changeTag: false, // Assuming no change tag for occupancy count
        },
    ];

    const tooltipMessages: { [key: string]: string } = {
        "Total Vehicles":
            "Displays the total number of vehicles registered in the fleet monitoring system, including active, idle, available, and stopped vehicles.",
        "Average KM Running":
            "Represents the average distance travelled per vehicle per day, calculated based on recent trip data.",
        "Average Running Time":
            "Shows the average duration each vehicle is actively running per day, based on engine activity and movement.",
        "Average Idling Time":
            "Measures the average time vehicles remain idle with the engine running but without movement.",
        "Occupancy Count":
            "Indicates the number of passengers currently occupying the vehicle, providing insights into vehicle utilization.",    
    };

    // Call the API and fetch vehicle details based on the provided values.
    useEffect(() => {
        fetchVehicleDetailsData({
            startDate,
            endDate,
            fleetId,
            vehicleId,
        });
    }, [startDate, endDate, fleetId, vehicleId]);

    // Log the received data once it is available.
    useEffect(() => {
        if (vehicleDetailsData?.data) {
            console.log('Vehicle details data:', vehicleDetailsData.data);
        }
    }, [vehicleDetailsData]);

    // Extract values from API response or fallback to defaults
    const totalKilometers = Number(vehicleDetailsData?.data?.totalKilometers ?? 0).toFixed(2);
    const totalAverageRunning = vehicleDetailsData?.data?.totalAverageRunning ?? 0;
    const roundedAverageRunning = Math.round(totalAverageRunning);
    const totalAverageIdleTime = vehicleDetailsData?.data?.totalAverageIdleTime ?? 0;
    const formattedIdleTime = formatTime(totalAverageIdleTime);
    const totalAverageDrivingTime = vehicleDetailsData?.data?.totalAverageDrivingTime ?? 0;
    const occupancyCount = vehicleDetailsData?.data?.occupancyCount ?? null;
    const formattedDrivingTime = formatTime(totalAverageDrivingTime);
    const currentAddress = vehicleDetailsData?.data?.currentAddress ?? 'NA';
    const vehicleName = vehicleDetailsData?.data?.vehicleName ?? 'NA';
    const currentLatitude = vehicleDetailsData?.data?.currentLatitude ?? 0;
    const currentLongitude = vehicleDetailsData?.data?.currentLongitude ?? 0;
    const latestUpdatedDate = vehicleDetailsData?.data?.latestUpdatedDate ?? 'NA';
    const theme = useTheme();
    // When opening the vehicle location modal, include current lat/lng values.
    const handleVehicleLocationOpen = () => {
        setSelectedVehicle({
            vehicleName,
            vehicleRegistrationNo: vehicleId, // vehicleId is used as the registration number
            currentLatitude, // keep if you need it elsewhere
            currentLongitude, // keep if you need it elsewhere
            latestUpdatedDate,
        });
        setIsVehicleLocationModalOpen(true);
    };

    const handleVehicleLocationCancel = () => {
        setIsVehicleLocationModalOpen(false);
    };

    tilesData[2].value = formattedIdleTime;
    tilesData[1].value = roundedAverageRunning;
    tilesData[0].value = totalKilometers;
    tilesData[3].value = formattedDrivingTime;
    tilesData[4].value =  occupancyCount==null? null : Math.max(0,occupancyCount); // Assuming occupancy count is part of the data

    console.log('Tiles data:', tilesData);
    console.log('Occupancy Count:', occupancyCount);
    const filteredTileData = useMemo(() => {
        return tilesData.filter((item, index) => {
            
        if (item.heading === "Occupancy Count" && occupancyCount == null) {
            console.log(occupancyCount, "Occupancy Count is null, skipping tile");
            return false;
         }
            const stateName = 'Stat' + (index + 1);
            if (tilesObj) {
                if (tilesObj[stateName].name === item.heading) {
                    console.log('Tile data:', item);
                    return tilesObj[stateName].status;
                }
                return false;
            } else {
                return true;
            }
        });
    }, [tilesObj, tilesData]);
    const displayTiles = filteredTileData.filter(
  (tile) => !(tile.heading === "OccupancyCount" && (tile.value === null || tile.value === undefined)));
   const lgColsClass = displayTiles.length === 5 ? "lg:grid-cols-5" : "lg:grid-cols-4";

    return (
        <>
            {/* First Row: Four Grid Cards */}
            <div className={`w-full grid grid-cols-1 md:grid-cols-2 ${lgColsClass} gap-2`}>
                {filteredTileData.map((tile, index) => (
                    <Paper
                        key={index}

                        sx={{
                            borderRadius: "12px",
                            cursor: tile.heading === "Total Vehicles" ? "pointer" : "default",
                            transition: "all 0.2s ease-in-out",
                            ...(tile.heading === "Total Vehicles" && {
                                "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                                },
                            }),
                        }}
                        className="border border-[#f0eff8] p-4 flex flex-col gap-1 items-start"
                    >
                        <div className="flex items-center w-full">
                            <img
                                src={tile.icon}
                                width={32}
                                height={32}
                                style={{
                                    background: theme.palette.selected,
                                    borderRadius: "8px", // Added border radius
                                }}
                                alt="icon"
                                className="p-2 mr-2"
                            />
                            <Typography
                                variant="body2"
                                className="flex-grow whitespace-nowrap overflow-hidden text-ellipsis"
                            >
                                {tile.heading}
                            </Typography>
                            {/* Wrap the info icon with a Tooltip */}
                            <Tooltip title={tooltipMessages[tile.heading]} arrow>
                                <img
                                    src={iIcon}
                                    alt="info icon"
                                    className="w-4 h-4 cursor-pointer"
                                />
                            </Tooltip>
                        </div>
                        <div className="grid items-center w-full grid-cols-2 md:flex md:flex-wrap md:justify-between">
                            {/* Left side: tile value */}
                            <Typography
                                variant="body1"
                                fontWeight={600}
                                className="ml-12 whitespace-nowrap overflow-hidden text-ellipsis"
                            >
                                {tile.value}
                            </Typography>
                            {/* Right side: change tag with swapped arrow and percentage */}
                            {/* {tile.changeTag && (
              <div
                className="flex items-center gap-1"
                style={{
                  width: "62px",
                  height: "26px",
                  padding: "4px 8px",
                  borderRadius: "8px",
                  backgroundColor: tile.changeIcon === arrowUp ? "#F7E1E1" : "#D9EDE6",
                }}
              >
                <img
                  src={tile.changeIcon === arrowUp ? arrowDown : arrowUp}
                  alt="change arrow"
                  style={{ width: "12px", height: "12px" }}
                />
                <Typography
                  variant="caption"
                  style={{
                    color: tile.changeIcon === arrowUp ? "#E41E2C" : "#2DAE50",
                    fontWeight: 500,
                  }}
                >
                  {tile.changeValue}
                </Typography>
              </div>
            )} */}
                        </div>

                    </Paper>
                ))}
            </div>

            {/* Title for the Second Row */}
            <div className='w-full py-2'>
                <Typography
                    sx={{
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '18px',
                        letterSpacing: '0%',
                        textTransform: 'uppercase',
                        padding: '4px 8px',
                        display: 'inline-block',
                        color: '#767676',
                    }}
                >
                    Location and Movement
                </Typography>
            </div>

            {/* Second Row: Two Additional Grid Cards */}
            <div className='grid grid-cols-2 gap-4 w-full py-8'>
                {/* GPS Position Card */}
                <div className='relative w-full h-full p-6 rounded-xl border border-[#f0eff8]'>
                    <div className='flex flex-col h-full justify-between'>
                        <div>
                            <div className='flex justify-between items-center'>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '18px',
                                        lineHeight: '27px',
                                        letterSpacing: '0%',
                                        padding: '4px 8px',
                                    }}
                                >
                                    GPS Position
                                </Typography>
                                <button
                                    className='flex items-center gap-2 border border-[#5547DD] text-[#5547DD] rounded-md px-3 py-1 font-medium'
                                    onClick={handleVehicleLocationOpen}
                                >
                                    <MapIcon className='w-[16px] h-[16px] text-[#5547DD]' />
                                    Show on Map
                                </button>
                            </div>
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    letterSpacing: '0%',
                                    color: '#767676',
                                    padding: '4px 8px',
                                    mt: 1,
                                }}
                            >
                                {currentAddress}
                            </Typography>
                        </div>
                    </div>
                </div>

                {/* Distance Traveled with MIL On Card */}
                {/* <div className='relative w-full h-full p-6 rounded-xl border border-[#f0eff8]'>
                    <div className='flex flex-col h-full'>
                        <div>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '18px',
                                    lineHeight: '27px',
                                    letterSpacing: '0%',
                                    padding: '4px 8px',
                                }}
                            >
                                Distance Travelled with MIL On
                            </Typography>
                        </div>
                        <Typography
                            sx={{
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '24px',
                                letterSpacing: '0%',
                                color: '#767676',
                                padding: '4px 8px',
                                mt: 1,
                            }}
                        >
                            0 Km
                        </Typography>
                    </div>
                </div> */}
            </div>

            {/* Dialog for Map */}
            <Dialog
                open={isVehicleLocationModalOpen}
                onClose={handleVehicleLocationCancel}
                aria-labelledby="vehicleLocation-dialog-title"
                aria-describedby="vehicleLocation-dialog-description"
                fullWidth
                maxWidth="md"
            >
                <DialogTitle id="vehicleLocation-dialog-title">
                    {selectedVehicle?.vehicleName || 'NA'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="vehicleLocation-dialog-description">
                        {selectedVehicle?.vehicleRegistrationNo || 'NA'}
                    </DialogContentText>
                    <Box
                        sx={{
                            width: '100%',
                            height: '750px',
                            mt: 2,
                        }}
                    >
                        {selectedVehicle && (
                            <MapPreview
                                // Transform the keys as required:
                                mapData={[
                                    {
                                        ...selectedVehicle,
                                        lastLatitude: currentLatitude,   // Here we convert currentLatitude into lastLatitude
                                        lastLongitude: currentLongitude, // And currentLongitude into lastLongitude
                                        lastUpdateTime: latestUpdatedDate,
                                    },
                                ]}
                                showStop={true}
                                zoomRatio={20}
                            />
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

        </>
    );
};

export default VehicleDashboardCard;
